import { CambioClausula, DiffPlantilla, Segmento, describirResumenDiff } from '@/lib/contratos';
import { classNames } from '@/lib/utils';

function Segmentos({ segmentos }: { segmentos: Segmento[] }) {
  return (
    <>
      {segmentos.map((s, i) =>
        s.tipo === 'igual'
          ? <span key={i}>{s.texto}</span>
          : s.tipo === 'anadido'
            ? <ins key={i} className="bg-emerald-100 text-emerald-900 no-underline rounded px-0.5">{s.texto}</ins>
            : <del key={i} className="bg-red-100 text-red-800 rounded px-0.5">{s.texto}</del>
      )}
    </>
  );
}

const ETIQUETA_CAMBIO: Record<string, string> = {
  titulo: 'título', texto: 'texto', tabla: 'tabla', variantes: 'variantes', formato: 'formato', nivel: 'nivel', seccion: 'movida de sección', bloque: 'bloque',
};

function Clausula({ c }: { c: CambioClausula }) {
  const color = c.estado === 'anadida' ? 'border-emerald-200 bg-emerald-50/50' : c.estado === 'eliminada' ? 'border-red-200 bg-red-50/40' : 'border-amber-200 bg-amber-50/40';
  const etiqueta = c.estado === 'anadida' ? 'Añadida' : c.estado === 'eliminada' ? 'Quitada' : 'Modificada';
  const cuerpo = c.despues || c.antes;
  return (
    <div className={classNames('rounded-lg border px-3 py-2 text-sm', color)}>
      <div className="flex flex-wrap items-center gap-1.5 mb-1">
        <span className={classNames('badge', c.estado === 'anadida' ? 'bg-emerald-100 text-emerald-800' : c.estado === 'eliminada' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800')}>{etiqueta}</span>
        {c.cambios.filter(x => x !== 'texto' && x !== 'titulo').map(x => <span key={x} className="badge bg-white/70 text-sereno-dark/60">{ETIQUETA_CAMBIO[x] || x}</span>)}
        <span className="text-[10px] font-mono text-sereno-dark/30 ml-auto">{c.id}</span>
      </div>
      {c.estado === 'modificada' ? (
        <p className="leading-relaxed">
          {(c.segmentosTitulo || c.titulo) && (
            <strong>{c.segmentosTitulo ? <Segmentos segmentos={c.segmentosTitulo} /> : c.titulo}{(c.despues?.texto || c.antes?.texto) ? ': ' : ''}</strong>
          )}
          {c.segmentosTexto ? <Segmentos segmentos={c.segmentosTexto} /> : (c.despues?.texto || '')}
        </p>
      ) : (
        <p className={classNames('leading-relaxed', c.estado === 'eliminada' && 'line-through text-sereno-dark/60')}>
          {cuerpo?.titulo && <strong>{cuerpo.titulo}{cuerpo.texto ? ': ' : ''}</strong>}{cuerpo?.texto}
          {cuerpo?.tabla && <span className="text-xs text-sereno-dark/50"> Tabla · {cuerpo.tabla.filas.length} filas</span>}
        </p>
      )}
      {c.estado === 'modificada' && c.cambios.includes('variantes') && (
        <div className="mt-2 text-xs text-sereno-dark/70">
          <p><span className="font-semibold">Variantes antes:</span> {(c.antes?.variantes || []).map(v => v.nombre || v.clave).join(', ') || 'ninguna'}</p>
          <p><span className="font-semibold">Variantes ahora:</span> {(c.despues?.variantes || []).map(v => v.nombre || v.clave).join(', ') || 'ninguna'}</p>
        </div>
      )}
      {c.estado === 'modificada' && c.cambios.includes('tabla') && (
        <p className="mt-1 text-xs text-sereno-dark/70">Tabla: {(c.antes?.tabla?.filas || []).length} filas → {(c.despues?.tabla?.filas || []).length} filas</p>
      )}
    </div>
  );
}

/** Qué cambió entre dos versiones de la plantilla, cláusula a cláusula. Solo se pintan las secciones con cambios. */
export default function DiffPlantillaVista({ diff }: { diff: DiffPlantilla }) {
  const conCambios = diff.secciones.filter(s => s.estado !== 'igual' || s.clausulas.some(c => c.estado !== 'igual'));
  const totalVars = diff.variables.anadidas.length + diff.variables.eliminadas.length + diff.variables.cambiadas.length;
  return (
    <div className="space-y-4">
      <p className="text-sm">
        <span className="font-semibold">v{diff.de} → v{diff.a}:</span> {describirResumenDiff(diff.resumen)}{totalVars ? ` · ${totalVars} variable${totalVars === 1 ? '' : 's'}` : ''}.
      </p>
      {totalVars > 0 && (
        <div className="rounded-lg bg-gray-50 p-3 text-xs space-y-0.5">
          {diff.variables.anadidas.length > 0 && <p><span className="badge bg-emerald-100 text-emerald-800 mr-1">Añadidas</span>{diff.variables.anadidas.join(', ')}</p>}
          {diff.variables.eliminadas.length > 0 && <p><span className="badge bg-red-100 text-red-700 mr-1">Quitadas</span>{diff.variables.eliminadas.join(', ')}</p>}
          {diff.variables.cambiadas.length > 0 && <p><span className="badge bg-amber-100 text-amber-800 mr-1">Cambiadas</span>{diff.variables.cambiadas.join(', ')}</p>}
        </div>
      )}
      {conCambios.length === 0 ? (
        <p className="text-sm text-sereno-dark/50">Las dos versiones tienen el mismo texto.</p>
      ) : conCambios.map(s => (
        <section key={s.id}>
          <h4 className="text-sm font-semibold mb-2 flex flex-wrap items-center gap-2">
            {s.estado === 'renombrada' && s.tituloAntes ? <><del className="text-red-700 font-normal">{s.tituloAntes}</del> <span>{s.titulo}</span></> : (s.titulo || 'Preámbulo')}
            {s.estado === 'anadida' && <span className="badge bg-emerald-100 text-emerald-800">Sección nueva</span>}
            {s.estado === 'eliminada' && <span className="badge bg-red-100 text-red-700">Sección quitada</span>}
          </h4>
          <div className="space-y-2">
            {s.clausulas.filter(c => c.estado !== 'igual').map(c => <Clausula key={c.id} c={c} />)}
          </div>
        </section>
      ))}
    </div>
  );
}
